/*
 * Bác sĩ khám — bước 2 của lượt tư vấn (họp 30/10): bác sĩ xem khai báo của khách và ghi chép của tư vấn viên,
 * xác định mức độ / vùng, chỉ định dịch vụ (thêm, bớt, số lượng), tích yêu cầu chuyên môn, ghi chú rồi ký.
 * Không hiện bất kỳ số tiền nào — giá, ưu đãi do tư vấn viên chốt ở bước Báo giá chính thức.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AutoComplete, Button, Checkbox, Input, InputNumber, Select, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CreditCard, Plus, Stethoscope, Trash2 } from 'lucide-react';
import { MANGS } from '../Telesales/lead/logic';
import {
  AREAS, CATALOG, LEVELS, REQS, RESULTS, allCodes, dmhm, doctorName, editQuote, hm, isToday, reqLabel, svcById, uid,
  type ApiSvc, type Exam, type QLine, type Visit,
} from './logic';
import { useTuVan } from './store';
import { useDoctors } from './receptionApi';
import { Card, FlowBar, Hint, LineName, NoVisit, NotStarted, Row, Shell, useDoctorView, useMe, useNow, useScreening, useVisit, who } from './shared';
import { useApiServices } from './catalog';
import { cardLine, cardOptions, useCardPackages } from './packages';
import { apiLine, draftQuote, fold } from './quoteDraft';
import ScreeningView from './ScreeningView';
import SignaturePad from './SignaturePad';

function Body({ v }: { v: Visit }) {
  const navigate = useNavigate();
  const me = useMe();
  const doctorView = useDoctorView();
  const { visits, update, meDoctor } = useTuVan();
  const apiSvcs = useApiServices();
  const pkgs = useCardPackages();
  const sc = useScreening(v);
  const q = v.quote;
  const started = v.status === 'consulting' || v.status === 'done';
  const locked = !!v.result;
  const signed = !!q?.exam.signedAt;
  // Chữ ký đang vẽ (chưa bấm Ký khám); key đổi để xoá khung khi huỷ ký
  const [sig, setSig] = useState<string>();
  const [sigKey, setSigKey] = useState(0);
  // Người ghi lịch sử: bác sĩ đang dùng máy, hoặc tư vấn viên ghi hộ
  const doctors = useDoctors();
  const actor = doctorView ? doctorName(meDoctor) : me.name;

  const begin = () => {
    // Yêu cầu chuyên môn lấy từ phiếu: chưa tải xong mà bắt đầu thì lưu thiếu (VD xét nghiệm HIV)
    if (sc.loading) { message.info('Đang tải phiếu khai báo, thử lại sau giây lát.'); return; }
    const d = draftQuote(v, who(v.consultant) || me.name, allCodes(visits));
    const doctor = v.invite?.doctor ?? (doctorView ? meDoctor : undefined);
    update(v.id, () => ({ quote: { ...d, exam: { ...d.exam, doctor, reqs: sc.reqs } } }), { text: `Bắt đầu khám · ${doctorName(doctor)}`, by: actor });
  };
  const setExam = (p: Partial<Exam>) => update(v.id, (x) => ({ quote: { ...x.quote!, exam: { ...x.quote!.exam, ...p, signedAt: undefined } } }));
  const setLines = (lines: QLine[]) => update(v.id, (x) => ({ quote: editQuote(x.quote!, { lines }) }));
  const addSvc = (id: string) => {
    const a = id.startsWith('api-') ? apiSvcs?.find((x) => `api-${x.id}` === id) : undefined;
    if (a) { setLines([...q!.lines, apiLine(a)]); return; }
    const sv = svcById(id);
    if (sv) setLines([...q!.lines, { id: uid(), svcId: sv.id, name: sv.name, unit: sv.unit, qty: 1, price: sv.price, core: sv.core }]);
  };
  // Chỉ định theo liệu trình: thẻ gói theo buổi (bác sĩ không thấy giá)
  const addCard = (id: string) => {
    const p = pkgs?.find((x) => x.id === id);
    if (p) setLines([...q!.lines, cardLine(p)]);
  };
  const sign = () => {
    if (!q?.exam.doctor || !q.exam.level) { message.warning('Chọn bác sĩ khám và mức độ trước khi ký.'); return; }
    if (!q.lines.length) { message.warning('Chỉ định ít nhất một dịch vụ.'); return; }
    if (!sig) { message.warning('Bác sĩ ký vào khung chữ ký trước khi bấm Ký khám.'); return; }
    update(v.id, (x) => ({ quote: { ...x.quote!, exam: { ...x.quote!.exam, signedAt: Date.now(), sign: sig } } }),
      { text: `${doctorName(q.exam.doctor)} khám và ký${q.exam.reqs?.length ? ` · ${q.exam.reqs.map(reqLabel).join(', ')}` : ''}`, by: doctorName(q.exam.doctor) });
    message.success('Đã ký khám — tư vấn viên chốt giá ở bước Báo giá chính thức.');
  };

  // Đã ký thì khoá phiếu khám; muốn sửa phải huỷ ký (có ghi lịch sử), sửa xong ký lại
  const unsign = () => {
    update(v.id, (x) => ({ quote: { ...x.quote!, exam: { ...x.quote!.exam, signedAt: undefined, sign: undefined } } }), { text: `${doctorName(q?.exam.doctor)} huỷ ký để sửa phiếu khám`, by: actor });
    setSig(undefined);
    setSigKey((k) => k + 1);
  };

  // Danh mục chỉ hiện tên dịch vụ, không giá
  const svcOptions = apiSvcs?.length
    ? Object.entries(apiSvcs.reduce<Record<string, ApiSvc[]>>((m, x) => { (m[x.cat || 'Khác'] ??= []).push(x); return m; }, {}))
      .map(([cat, list]) => ({ label: cat, options: list.map((x) => ({ value: `api-${x.id}`, label: x.name })) }))
    : [v.mang, ...MANGS.filter((m) => m !== v.mang)].map((m) => ({ label: m, options: CATALOG[m].map((x) => ({ value: x.id, label: `${x.name} (${x.unit})` })) }));
  const suggested = sc.reqs.filter((r) => !q?.exam.reqs?.includes(r));

  const columns: ColumnsType<QLine> = [
    { title: 'Dịch vụ chỉ định', key: 'name', render: (_, l) => <LineName l={l} noMoney /> },
    { title: 'ĐVT', dataIndex: 'unit', width: 70 },
    { title: 'Số lượng', key: 'qty', width: 110, render: (_, l) => <InputNumber size="small" min={1} max={99} value={l.qty} disabled={locked || signed} onChange={(x) => setLines(q!.lines.map((y) => (y.id === l.id ? { ...y, qty: x ?? 1 } : y)))} className="!w-[80px]" /> },
    { title: '', key: 'del', width: 44, render: (_, l) => <Button size="small" type="text" danger disabled={locked || signed} icon={<Trash2 size={14} />} onClick={() => setLines(q!.lines.filter((x) => x.id !== l.id))} /> },
  ];

  return (
    <>
      <NotStarted v={v} />
      <div className="grid grid-cols-1 items-start gap-3 sm:gap-4 xl:grid-cols-5">
        <div className="space-y-3 sm:space-y-4 xl:col-span-2">
          <ScreeningView v={v} />
          <Card title="Tư vấn viên ghi">
            <div className="space-y-2">
              <Row label="Mảng · nhu cầu"><b>{v.mang}</b> · {v.need}</Row>
              <Row label="Tư vấn viên">{who(v.consultant)}{v.room ? ` · ${v.room}` : ''}</Row>
              {v.needs?.map((n) => <Row key={n.label} label={n.label}>{n.value}</Row>)}
            </div>
            <div className="mt-3 whitespace-pre-line rounded-lg bg-gray-50 p-3 text-[13px] text-gray-700 dark:bg-gray-700/40 dark:text-gray-200">{v.note.trim() || <span className="text-gray-400">Tư vấn viên chưa ghi chép sàng lọc.</span>}</div>
          </Card>
        </div>

        <div className="xl:col-span-3">
          <Card title="Khám và chỉ định" extra={signed ? <Tag color="green" className="!m-0">Đã ký {dmhm(q!.exam.signedAt)}</Tag> : q ? <Tag color="orange" className="!m-0">Chưa ký</Tag> : null}>
            {!q ? (
              <div className="flex flex-col items-center gap-3 py-8 text-center">
                <Stethoscope size={32} className="text-gray-300" />
                <p className="max-w-md text-sm text-gray-500">
                  {v.invite ? `Tư vấn viên đã mời ${doctorName(v.invite.doctor)} lúc ${hm(v.invite.at)}.` : 'Chưa mời bác sĩ.'} Dịch vụ lấy sẵn theo lịch hẹn / báo giá online; bác sĩ thêm, bớt, chỉnh số lượng.
                </p>
                <Button type="primary" disabled={!started} onClick={begin}>Bắt đầu khám</Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <label className="text-[13px] text-gray-500">Bác sĩ khám
                    <Select value={q.exam.doctor} disabled={locked || signed} onChange={(d) => setExam({ doctor: d })} className="mt-1 w-full" placeholder="Chọn bác sĩ" options={doctors.map((d) => ({ value: d.id, label: d.name }))} />
                  </label>
                  <label className="text-[13px] text-gray-500">Mức độ
                    <Select value={q.exam.level} disabled={locked || signed} onChange={(x) => setExam({ level: x })} className="mt-1 w-full" placeholder="Chọn mức độ" options={LEVELS.map((x) => ({ value: x, label: x }))} />
                  </label>
                  <label className="text-[13px] text-gray-500">Vùng
                    <AutoComplete value={q.exam.area} disabled={locked || signed} onChange={(x) => setExam({ area: x })} className="mt-1 w-full" placeholder="Chọn hoặc gõ vùng"
                      options={AREAS[v.mang].map((x) => ({ value: x }))} filterOption={(input, o) => fold(String(o?.value)).includes(fold(input))} />
                  </label>
                </div>

                <div>
                  <div className="mb-1.5 text-[13px] font-semibold text-gray-700 dark:text-gray-200">Dịch vụ chỉ định</div>
                  <Table rowKey="id" size="small" columns={columns} dataSource={q.lines} pagination={false} locale={{ emptyText: 'Chưa có dịch vụ' }} />
                  {!locked && !signed && (
                    <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                      <Select key={q.lines.length} showSearch placeholder={<span className="inline-flex items-center gap-1"><Plus size={14} />Thêm dịch vụ (xét nghiệm, gói kèm…)</span>} onChange={addSvc} className="w-full sm:w-[340px]"
                        filterOption={(input, o) => !!o && 'value' in o && fold(String(o.label)).includes(fold(input))}
                        popupClassName="[&_.ant-select-item-option-content]:!whitespace-normal" listHeight={320} loading={!apiSvcs} options={svcOptions} />
                      <Select key={`c${q.lines.length}`} showSearch placeholder={<span className="inline-flex items-center gap-1"><CreditCard size={14} />Chỉ định liệu trình (thẻ)</span>} onChange={addCard} className="w-full sm:w-[340px]"
                        filterOption={(input, o) => !!o && 'value' in o && fold(String(o.label)).includes(fold(input))}
                        popupClassName="[&_.ant-select-item-option-content]:!whitespace-normal" listHeight={320}
                        loading={!pkgs} notFoundContent={pkgs ? 'Chưa có gói đang bán' : undefined} options={cardOptions(pkgs ?? [], false)} />
                    </div>
                  )}
                </div>

                <div>
                  <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-[13px] font-semibold text-gray-700 dark:text-gray-200">Yêu cầu chuyên môn</span>
                    {suggested.length > 0 && !locked && !signed && (
                      <Button size="small" type="link" className="!px-0" onClick={() => setExam({ reqs: [...(q.exam.reqs ?? []), ...suggested] })}>
                        Tích theo khai báo: {suggested.map(reqLabel).join(', ')}
                      </Button>
                    )}
                  </div>
                  <Checkbox.Group value={q.exam.reqs ?? []} disabled={locked || signed} onChange={(x) => setExam({ reqs: x as string[] })} className="grid grid-cols-1 gap-1.5 sm:grid-cols-2"
                    options={REQS.map((r) => ({ value: r.key, label: r.label }))} />
                  <Hint>Tích để bộ phận chuyên môn nhận được (VD: chuẩn bị gây mê, dụng cụ riêng). Ghi chú tự do bên dưới chỉ để đọc.</Hint>
                </div>

                <label className="block text-[13px] font-semibold text-gray-700 dark:text-gray-200">Nhận xét / ghi chú của bác sĩ
                  <Input.TextArea rows={3} value={q.exam.note} disabled={locked || signed} onChange={(e) => setExam({ note: e.target.value })} className="mt-1 !font-normal" placeholder="VD: vùng đỉnh hói mức 3, nang trội ổn, cần điều trị nền 1 tháng trước khi cấy" />
                </label>

                {/* Chữ ký tay của bác sĩ: ký trong khung rồi bấm "Ký khám"; đã ký thì hiện ảnh chữ ký */}
                <div className="border-t border-gray-100 pt-3 dark:border-gray-700">
                  <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2 text-[13px] font-semibold text-gray-700 dark:text-gray-200">
                    <span>Chữ ký bác sĩ{!signed && <span className="text-red-500"> *</span>}</span>
                    {signed && <span className="text-xs font-normal text-gray-500">{doctorName(q.exam.doctor)} · {dmhm(q.exam.signedAt)}</span>}
                  </div>
                  {signed ? (
                    q.exam.sign
                      ? <img src={q.exam.sign} alt={`Chữ ký ${doctorName(q.exam.doctor)}`} className="h-[160px] w-full rounded-xl border border-green-200 bg-white object-contain dark:border-green-500/30" />
                      : <div className="flex h-[60px] items-center justify-center rounded-xl border border-green-200 bg-green-50/60 text-xs text-green-700 dark:border-green-500/30 dark:bg-green-500/10">Đã ký xác nhận (ký trước khi có khung ký tay)</div>
                  ) : (
                    <div>
                      <SignaturePad key={sigKey} value={sig} onChange={setSig} hint={`${doctorName(q.exam.doctor)} ký trong khung`} />
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-700">
                  {signed ? (
                    <>
                      {!locked && <Button onClick={unsign}>Huỷ ký để sửa</Button>}
                      {!doctorView && <Button type="primary" onClick={() => navigate(`/phieu-bao-gia?v=${v.id}`)}>Sang Báo giá chính thức</Button>}
                    </>
                  ) : (
                    <Button type="primary" icon={<Stethoscope size={14} />} onClick={sign} disabled={!started || locked}>Ký khám</Button>
                  )}
                </div>
                <Hint>Bác sĩ không thấy giá. Tư vấn viên chốt giá, ưu đãi ở bước Báo giá; thêm, bớt dịch vụ hoặc đổi số lượng sau khi ký thì bác sĩ phải ký lại.</Hint>
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

/** Hàng chờ khám hôm nay: khách đã được mời / đã bắt đầu khám, bác sĩ xem với vai trò bác sĩ chỉ thấy khách của mình */
const rank = (v: Visit) => (v.quote?.exam.signedAt ? 1 : v.result ? 2 : 0);

function Queue() {
  const now = useNow();
  const { visits, branchId, meDoctor } = useTuVan();
  const doctorView = useDoctorView();
  const { v: cur, pick } = useVisit();
  const rows = useMemo(() => visits
    .filter((v) => v.branchId === branchId && isToday(v.checkInAt ?? v.createdAt, now) && (v.invite || v.quote))
    .filter((v) => !doctorView || (v.quote?.exam.doctor ?? v.invite?.doctor) === meDoctor)
    // Chờ khám lên đầu, rồi đã ký, cuối cùng khách đã ghi kết quả mà không cần ký (chưa có nhu cầu, bác sĩ chưa đồng ý…)
    .sort((a, b) => rank(a) - rank(b) || (a.invite?.at ?? 0) - (b.invite?.at ?? 0)),
  [visits, branchId, now, doctorView, meDoctor]);
  return (
    <Card title={doctorView ? `Khách chờ ${doctorName(meDoctor)} khám` : 'Khách mời bác sĩ khám hôm nay'}>
      {rows.length ? (
        <div className="flex flex-wrap gap-2">
          {rows.map((v) => {
            const ok = !!v.quote?.exam.signedAt;
            return (
              <button key={v.id} type="button" onClick={() => pick(v.id)}
                className={`rounded-lg border px-3 py-2 text-left text-[13px] transition ${v.id === cur?.id ? 'border-[#3e91ab] bg-[#3e91ab]/10' : 'border-gray-200 hover:border-[#3e91ab] dark:border-gray-700'}`}>
                <div className="font-semibold text-gray-800 dark:text-gray-100">{v.cust.name}</div>
                <div className="text-xs text-gray-500">{v.mang} · {v.room ?? '—'} · {ok ? <span className="text-green-600">đã ký</span> : v.result ? <span className="text-gray-400">không ký · {RESULTS[v.result.kind].short}</span> : <span className="text-amber-600">chờ khám{v.invite ? ` từ ${hm(v.invite.at)}` : ''}</span>}</div>
              </button>
            );
          })}
        </div>
      ) : <Hint>Chưa có khách được mời khám.</Hint>}
    </Card>
  );
}

export default function BacSiKhamPage() {
  const { v } = useVisit();
  return (
    <Shell title="Bác sĩ khám" sub="Xem khai báo của khách, chỉ định dịch vụ, tích yêu cầu chuyên môn và ký — không hiện giá">
      <Queue />
      <FlowBar v={v} step="exam" />
      {v ? <Body key={v.id} v={v} /> : <NoVisit />}
    </Shell>
  );
}
